import React, { useEffect, useId, useRef, useState } from 'react'
import styled from 'styled-components'
import { Button, ButtonGroup } from '@mui/material'
import { isMacOs, isWindows } from 'react-device-detect'
import { UIStyles } from '../../../theme'
import { pillLooks } from '../../UIComponents/StyledButtons'
import { closeButtonLook } from '../../UIComponents/CloseModalButton'
import { pillButtonStyle, pillGroupStyle, pillSelectedButtonStyle } from '../../UIComponents/StyledButtonGroup'
import {
  hasSeenLayiNotificationsSetup,
  hideLayiNotificationsSetup,
  isLayiNotificationsSupported,
  markLayiNotificationsSetupSeen,
  markLayiNotificationsVerified,
  showLayiNotification
} from '../../../chatbot/layiNotifications'

// LAYI notifications, opened by their switch (user menu row or invite card, chatbot/useLayiNotificationsSwitch.js)
// or by «Configure» in the user menu. Setup, on every activation until the user hides it: the notifications are
// already on, and the user sends a test notification and says whether it arrived. "Yes" is recorded and ends with
// the ready line; "No" opens the guide for their operating system, preselected when it is detected, while sending
// again and "Yes" stay available. From the second time a user sees the setup in a browser, every step also offers
// "Don't show again": recorded for that user, it closes the modal, and their activations there only turn the
// notifications on. Blocked: why the browser keeps them off. Closing in any other way (the cross, Escape, the
// final OK) leaves the notifications as they are and hides nothing; a click outside does not close it. ModalBase
// gives the title, which labels the dialog, and the focus trap.

const SEEN = 'seen'
const NOT_SEEN = 'notSeen'

// The system names are brands and stay untranslated.
const OS_GUIDES = [
  { os: 'mac', label: 'macOS', guide: 'layi_notifications_guide_mac' },
  { os: 'windows', label: 'Windows', guide: 'layi_notifications_guide_windows' }
]

// The guide of the system the browser runs on is shown first; on any other system none is chosen.
const detectOs = () => (isMacOs ? 'mac' : isWindows ? 'windows' : null)

const focusRing = `
  &:focus-visible {
    outline: 2px solid ${UIStyles.mintColor};
    outline-offset: 2px;
  }
`

const Wrapper = styled.div`
  padding: 0 32px 4px;
  color: ${UIStyles.whiteColor};
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`

// The modal close cross (closeButtonLook), in the corner of the dialog paper, which is the positioned box.
const CloseButton = styled.button`
  ${closeButtonLook};
  position: absolute;
  top: 14px;
  right: 14px;
  width: 25px;
  height: 25px;
  padding: 0;
  border: none;
  font-size: 13px;
  line-height: 25px;
  cursor: pointer;
  ${focusRing};
`

const Paragraph = styled.p`
  margin: 0 0 16px;
`

const Question = styled.p`
  margin: 20px 0 12px;
  font-weight: 700;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
`

// The pills of the app's modals (StyledButtons pillLooks): confirm, outline and neutral.
const PillButton = styled.button`
  border: none;
  padding: 8px 18px;
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  ${props => pillLooks[props.look]};
  ${focusRing};
`

const Ready = styled.p`
  margin: 4px 0 20px;
  font-size: 15px;
  & > i {
    margin-right: 8px;
    color: ${UIStyles.mintColor};
  }
`

// Below a mint rule, like the separators inside the other panels and modals.
const Guide = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${UIStyles.mintBorder};
`

const GuideIntro = styled.p`
  margin: 0 0 12px;
  color: ${UIStyles.whiteTransparent08};
`

const GuideText = styled.p`
  margin: 14px 0 0;
`

// "Don't show again": a bare text button under every step, in the soft white of the secondary texts, so it never
// competes with the pills.
const HideButton = styled.button`
  display: block;
  margin: 20px auto 0;
  padding: 4px 8px;
  border: none;
  background: ${UIStyles.transparent};
  color: ${UIStyles.whiteTransparent08};
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  line-height: 20px;
  cursor: pointer;
  transition: color 0.15s;
  &:hover {
    color: ${UIStyles.whiteColor};
    text-decoration: underline;
  }
  ${focusRing};
`

// The approved pill toggle (StyledButtonGroup's pill look), keeping each brand's own capitals.
const osOptionSx = selected => [
  pillButtonStyle,
  selected && pillSelectedButtonStyle,
  {
    '&&&': { textTransform: 'none', minWidth: '96px' },
    '&&&.Mui-focusVisible': { outline: `2px solid ${UIStyles.mintColor}`, outlineOffset: '2px' }
  }
]

export default function LayiNotificationsModal({ t, closeModal, userId, canUseLayi, blocked = false }) {
  const allowed = Boolean(canUseLayi) && isLayiNotificationsSupported()
  const [sent, setSent] = useState(false)
  const [answer, setAnswer] = useState(null)
  const [os, setOs] = useState(detectOs)
  // Read before this opening is recorded: the first time a user sees the setup here it cannot be hidden yet.
  const [canHide] = useState(() => hasSeenLayiNotificationsSetup(userId))
  const setup = allowed && !blocked
  const osGroupRef = useRef(null)
  const textId = useId()
  const readyId = useId()
  const guideIntroId = useId()

  // Like the row and the card that open it: LAYI users on a browser that can notify, or nothing at all.
  useEffect(() => {
    if (!allowed) closeModal()
  }, [allowed, closeModal])

  // Recorded once the setup shows: the blocked notice, and a modal that closes itself unseen, do not count.
  useEffect(() => {
    if (setup) markLayiNotificationsSetupSeen(userId)
  }, [setup, userId])

  // "No" leaves once answered: the keyboard focus moves on to the choice of system.
  useEffect(() => {
    if (answer !== NOT_SEEN || !osGroupRef.current) return
    const target = osGroupRef.current.querySelector('[aria-pressed="true"]') || osGroupRef.current.querySelector('button')
    if (target) target.focus()
  }, [answer])

  if (!allowed) return null

  const closeButton = (
    <CloseButton type="button" aria-label={t('Close')} onClick={closeModal}>
      <i className="fas fa-times" aria-hidden="true" />
    </CloseButton>
  )

  if (blocked) {
    return (
      <Wrapper data-testid="layi-notifications-modal">
        {closeButton}
        <Paragraph id={textId}>{t('layi_notifications_blocked')}</Paragraph>
        <Actions>
          <PillButton type="button" look="confirm" autoFocus aria-describedby={textId} onClick={closeModal}>
            {t('ok')}
          </PillButton>
        </Actions>
      </Wrapper>
    )
  }

  const sendTest = () => {
    showLayiNotification({ title: t('layi_notifications_test_title'), body: t('layi_notifications_test_body') })
    setSent(true)
  }

  const confirmSeen = () => {
    markLayiNotificationsVerified(userId)
    setAnswer(SEEN)
  }

  const hideSetup = () => {
    hideLayiNotificationsSetup(userId)
    closeModal()
  }

  const ready = answer === SEEN
  const guide = OS_GUIDES.find(option => option.os === os)

  return (
    <Wrapper data-testid="layi-notifications-modal">
      {closeButton}
      {!ready && (
        <>
          <Paragraph id={textId}>{t('layi_notifications_setup_intro')}</Paragraph>
          <Actions>
            <PillButton type="button" look={sent ? 'outline' : 'confirm'} autoFocus aria-describedby={textId} onClick={sendTest}>
              {t('layi_notifications_test_send')}
            </PillButton>
          </Actions>
        </>
      )}
      {/* What each step brings is read out: the question, the guide, the ready line. */}
      <div aria-live="polite">
        {ready && (
          <>
            <Ready id={readyId}>
              <i className="fa-solid fa-circle-check" aria-hidden="true" />
              {t('layi_notifications_ready')}
            </Ready>
            <Actions>
              <PillButton type="button" look="confirm" autoFocus aria-describedby={readyId} onClick={closeModal}>
                {t('ok')}
              </PillButton>
            </Actions>
          </>
        )}
        {!ready && sent && (
          <>
            <Question>{t('layi_notifications_test_question')}</Question>
            <Actions>
              <PillButton type="button" look="confirm" onClick={confirmSeen}>
                {t('layi_notifications_test_seen')}
              </PillButton>
              {answer !== NOT_SEEN && (
                <PillButton type="button" look="neutral" onClick={() => setAnswer(NOT_SEEN)}>
                  {t('layi_notifications_test_not_seen')}
                </PillButton>
              )}
            </Actions>
          </>
        )}
        {!ready && answer === NOT_SEEN && (
          <Guide>
            <GuideIntro id={guideIntroId}>{t('layi_notifications_guide_intro')}</GuideIntro>
            <ButtonGroup ref={osGroupRef} size="small" aria-labelledby={guideIntroId} sx={pillGroupStyle}>
              {OS_GUIDES.map(option => (
                <Button key={option.os} type="button" aria-pressed={option.os === os} onClick={() => setOs(option.os)} sx={osOptionSx(option.os === os)}>
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
            {guide && <GuideText>{t(guide.guide)}</GuideText>}
          </Guide>
        )}
      </div>
      {canHide && (
        <HideButton type="button" onClick={hideSetup}>
          {t('layi_notifications_setup_hide')}
        </HideButton>
      )}
    </Wrapper>
  )
}
